'use client'
// =============================================================================
// HomeLegalStats — Tableau de bord Legal Engine sur l'accueil.
// Cadrans SVG (jauges circulaires), compteurs animés (count-up qui défile et
// s'arrête sur le vrai chiffre), santé des villes en anneaux, jobs en barres.
// Données : /api/legal-admin/stats + /api/legal-admin/cities.
// =============================================================================
import { useState, useEffect } from 'react'
import { useCountUp } from '@/hooks/useCountUp'

const ADM = '/api/legal-admin'

interface Stats {
  legalEngine?: { cities: number; documents: number; articles: number; zones: number }
  prisma?: { cities: number; totalDocuments: number; totalRules: number; avgRuleConfidence: number; documentsByStatus: Record<string, number>; jobsByStatus: Record<string, number> }
}

interface City {
  slug: string; name?: string; nom?: string
  docCount?: number; articleCount?: number; zoneCount?: number; ruleCount?: number
}

function statusColor(s: string) {
  if (s === 'done' || s === 'completed' || s === 'ok') return '#34d399'
  if (s === 'running' || s === 'processing') return '#f5a623'
  if (s === 'error' || s === 'failed') return '#ef4444'
  if (s === 'pending') return '#64748b'
  return '#4f7cff'
}

// ─── Cadran : arc 270° gradué + compteur au centre ────────────────────────────

function Dial({ value, max, label, color, unit = '' }: {
  value: number; max: number; label: string; color: string; unit?: string
}) {
  const shown = useCountUp(value)
  const size = 118, cx = size / 2, cy = size / 2, r = 46
  // Arc 270° : de 135° à 405°
  const arc = (deg: number) => {
    const a = ((deg - 90) * Math.PI) / 180
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
  }
  const startDeg = 225, sweep = 270
  const shownPct = max > 0 ? Math.min(shown / max, 1) : 0
  const endDeg = startDeg + sweep * shownPct
  const [x0, y0] = arc(startDeg)
  const [x1, y1] = arc(startDeg + sweep)
  const [xv, yv] = arc(endDeg)
  const largeBg = sweep > 180 ? 1 : 0
  const largeVal = sweep * shownPct > 180 ? 1 : 0

  // Graduations (13 ticks sur 270°)
  const ticks = Array.from({ length: 13 }, (_, i) => {
    const deg = startDeg + (sweep * i) / 12
    const a = ((deg - 90) * Math.PI) / 180
    const r1 = r + 6, r2 = r + (i % 3 === 0 ? 12 : 9)
    return { x1: cx + r1 * Math.cos(a), y1: cy + r1 * Math.sin(a), x2: cx + r2 * Math.cos(a), y2: cy + r2 * Math.sin(a), major: i % 3 === 0 }
  })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '14px 8px 10px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${color}22`, borderRadius: 14, minWidth: 0 }}>
      <svg width={size} height={size * 0.86} viewBox={`0 0 ${size} ${size * 0.86}`}>
        {/* graduations */}
        {ticks.map((t, i) => (
          <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
            stroke={t.major ? `${color}66` : 'rgba(255,255,255,0.12)'} strokeWidth={t.major ? 1.5 : 1} />
        ))}
        {/* piste */}
        <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${largeBg} 1 ${x1} ${y1}`}
          fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" strokeLinecap="round" />
        {/* valeur */}
        {shownPct > 0.005 && (
          <path d={`M ${x0} ${y0} A ${r} ${r} 0 ${largeVal} 1 ${xv} ${yv}`}
            fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
            style={{ filter: `drop-shadow(0 0 5px ${color}70)` }} />
        )}
        {/* compteur */}
        <text x={cx} y={cy + 2} textAnchor="middle" fill={color}
          style={{ fontSize: value >= 1000 ? 19 : 24, fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>
          {shown.toLocaleString('fr-CA')}{unit}
        </text>
        <text x={cx} y={cy + 18} textAnchor="middle" fill="rgba(255,255,255,0.28)"
          style={{ fontSize: 8, fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.08em' }}>
          / {max.toLocaleString('fr-CA')}{unit}
        </text>
      </svg>
      <div style={{ fontSize: 9, color: '#64748b', fontFamily: 'var(--font-mono, monospace)', textTransform: 'uppercase', letterSpacing: '0.1em', textAlign: 'center', marginTop: 2 }}>
        {label}
      </div>
    </div>
  )
}

// ─── Anneau de santé ville : % au centre + piliers ────────────────────────────

function CityRing({ city }: { city: City }) {
  const pillars = [city.docCount, city.articleCount, city.zoneCount, city.ruleCount].map(v => (v ?? 0) > 0)
  const score = Math.round(pillars.filter(Boolean).length / 4 * 100)
  const color = score >= 75 ? '#34d399' : score >= 50 ? '#f5a623' : score > 0 ? '#fb7185' : '#475569'
  const shown = useCountUp(score, 1600)
  const size = 84, r = 34, c = 2 * Math.PI * r
  const off = c * (1 - shown / 100)

  return (
    <a href={`/technologue/villes?tab=municipalites&city=${encodeURIComponent(city.name ?? city.slug)}`}
      style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 18px', background: 'rgba(255,255,255,0.02)', border: `1px solid ${color}30`, borderRadius: 14, textDecoration: 'none', flex: '1 1 300px', minWidth: 0 }}>
      <svg width={size} height={size} style={{ flexShrink: 0 }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.07)" strokeWidth="7" />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth="7" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={off}
          transform={`rotate(-90 ${size/2} ${size/2})`}
          style={{ filter: `drop-shadow(0 0 5px ${color}60)` }} />
        <text x={size/2} y={size/2 + 5} textAnchor="middle" fill={color}
          style={{ fontSize: 17, fontWeight: 800, fontFamily: 'var(--font-mono, monospace)' }}>
          {shown}%
        </text>
      </svg>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#e2e8f0', marginBottom: 8, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {city.name ?? city.nom ?? city.slug}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '5px 14px' }}>
          {[
            { icon: '📄', label: 'docs',   val: city.docCount ?? 0,     c: '#94a3b8' },
            { icon: '§',  label: 'articles', val: city.articleCount ?? 0, c: '#2dd4bf' },
            { icon: '▦',  label: 'zones',  val: city.zoneCount ?? 0,    c: '#34d399' },
            { icon: '⚖',  label: 'règles', val: city.ruleCount ?? 0,    c: '#f5a623' },
          ].map(p => (
            <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontFamily: 'var(--font-mono, monospace)' }}>
              <span style={{ color: p.c, width: 14, textAlign: 'center' }}>{p.icon}</span>
              <span style={{ color: p.c, fontWeight: 700 }}>{p.val.toLocaleString('fr-CA')}</span>
              <span style={{ color: '#475569', fontSize: 9 }}>{p.label}</span>
            </div>
          ))}
        </div>
      </div>
    </a>
  )
}

// ─── Barre de jobs animée ─────────────────────────────────────────────────────

function JobBar({ status, count, max }: { status: string; count: number; max: number }) {
  const color = statusColor(status)
  const shown = useCountUp(count, 1200)
  const [w, setW] = useState(0)
  useEffect(() => {
    const t = setTimeout(() => setW(max > 0 ? (count / max) * 100 : 0), 100)
    return () => clearTimeout(t)
  }, [count, max])
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ width: 70, fontSize: 10, color: '#64748b', fontFamily: 'var(--font-mono, monospace)', textAlign: 'right', flexShrink: 0 }}>{status}</span>
      <div style={{ flex: 1, height: 14, background: 'rgba(255,255,255,0.05)', borderRadius: 7, overflow: 'hidden' }}>
        <div style={{ width: `${w}%`, height: '100%', borderRadius: 7, background: `linear-gradient(90deg, ${color}cc, ${color})`, transition: 'width 1.2s cubic-bezier(0.22, 1, 0.36, 1)', boxShadow: `0 0 8px ${color}50` }} />
      </div>
      <span style={{ width: 44, fontSize: 15, fontWeight: 800, color, fontFamily: 'var(--font-mono, monospace)', flexShrink: 0 }}>{shown}</span>
    </div>
  )
}

// ─── Bloc principal ───────────────────────────────────────────────────────────

export function HomeLegalStats() {
  const [stats, setStats]   = useState<Stats | null>(null)
  const [cities, setCities] = useState<City[]>([])

  useEffect(() => {
    fetch(`${ADM}/stats`).then(r => r.ok ? r.json() : null).then(setStats).catch(() => {})
    fetch(`${ADM}/cities?page=1&limit=100`)
      .then(r => r.ok ? r.json() : null)
      .then(d => setCities(Array.isArray(d?.items) ? d.items : []))
      .catch(() => {})
  }, [])

  if (!stats) return null

  const le = stats.legalEngine
  const pr = stats.prisma
  // Échelle du cadran : prochain palier « rond » au-dessus de la valeur
  const niceMax = (v: number) => {
    if (v <= 0) return 10
    const mag = Math.pow(10, Math.floor(Math.log10(v)))
    return Math.ceil(v / mag) * mag
  }

  const dials = [
    { label: 'Villes analysées',    value: le?.cities    ?? 0, color: '#4f7cff' },
    { label: 'Documents ingérés',   value: le?.documents ?? 0, color: '#a78bfa' },
    { label: 'Articles extraits',   value: le?.articles  ?? 0, color: '#2dd4bf' },
    { label: 'Zones urbanistiques', value: le?.zones     ?? 0, color: '#34d399' },
    { label: 'Règles structurées',  value: pr?.totalRules ?? 0, color: '#f5a623' },
  ]

  const confiance = Math.round((pr?.avgRuleConfidence ?? 0) * 100)
  const jobs = Object.entries(pr?.jobsByStatus ?? {}).sort((a, b) => (b[1] as number) - (a[1] as number))
  const jobMax = jobs.reduce((m, [, c]) => Math.max(m, c as number), 0)

  return (
    <div>
      {/* Cadrans Legal Engine */}
      

      {/* Santé des villes + Jobs */}
      
    </div>
  )
}
